/**
 * Category Tree Node Component
 *
 * Individual node in the category tree with expand/collapse and actions.
 */

import { useState } from 'react';
import { ChevronRight, ChevronDown, Plus, Edit2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui';
import { useArchiveCategory } from '../../raw-products/hooks/useCategories';
import type { CategoryTreeNode as CategoryTreeNodeType } from '@bstock/shared';

interface CategoryTreeNodeProps {
  node: CategoryTreeNodeType;
  depth: number;
  isExpanded: boolean;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  onAddChild: (parentId: string) => void;
  onEdit: (category: CategoryTreeNodeType) => void;
}

export function CategoryTreeNode({
  node,
  depth,
  isExpanded,
  expandedIds,
  onToggleExpand,
  onAddChild,
  onEdit,
}: CategoryTreeNodeProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const deleteMutation = useArchiveCategory();

  const hasChildren = node.children && node.children.length > 0;
  const paddingLeft = 16 + depth * 24;

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(node.id);
      setIsDeleteModalOpen(false);
    } catch {
      // Error handled by mutation
    }
  };

  // Effective color (inherited from parent if not defined)
  const effectiveColor = node.color ?? '#9E9E9E';

  return (
    <>
      <div
        className="group flex items-center gap-3 py-3 hover:bg-neutral-50 transition-colors"
        style={{ paddingLeft }}
      >
        {/* Expand/Collapse button */}
        <button
          onClick={() => hasChildren && onToggleExpand(node.id)}
          className={`p-1 rounded transition-all ${
            hasChildren
              ? 'hover:bg-neutral-100 cursor-pointer'
              : 'opacity-0 cursor-default'
          }`}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="w-4 h-4 text-neutral-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-neutral-500" />
            )
          ) : (
            <div className="w-4 h-4" />
          )}
        </button>

        {/* Color indicator */}
        <span
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: effectiveColor }}
        />

        {/* Name and code */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-dark-500 truncate">
            {node.name}
          </p>
          <p className="text-xs text-neutral-500 font-mono">
            {node.code}
          </p>
        </div>

        {/* Product count */}
        <span className="text-xs text-neutral-500 mr-4">
          {node.productCount} produit{node.productCount !== 1 ? 's' : ''}
        </span>

        {/* Actions (visible on hover) */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity pr-4">
          {/* Hide add child button at max depth (3 levels: 0, 1, 2) */}
          {node.depth < 2 && (
            <button
              onClick={() => onAddChild(node.id)}
              className="p-2 text-neutral-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all"
              title="Ajouter une sous-categorie"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}

          <button
            onClick={() => onEdit(node)}
            className="p-2 text-neutral-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all"
            title="Modifier"
          >
            <Edit2 className="w-4 h-4" />
          </button>

          <button
            onClick={() => setIsDeleteModalOpen(true)}
            className="p-2 text-neutral-500 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-all"
            title="Archiver"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {node.children!.map((child) => (
            <CategoryTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              isExpanded={expandedIds.has(child.id)}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              onAddChild={onAddChild}
              onEdit={onEdit}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-dark-900/40"
            onClick={() => setIsDeleteModalOpen(false)}
          />
          <div className="relative z-10 bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-dark-500 mb-2">
              Archiver la categorie
            </h3>
            <p className="text-sm text-neutral-600 mb-6">
              Etes-vous sur de vouloir archiver "{node.name}" ?
              {node.productCount > 0 && (
                <span className="block mt-2 text-warning-600">
                  Attention: Cette categorie contient {node.productCount} produit(s).
                </span>
              )}
              {hasChildren && (
                <span className="block mt-2 text-warning-600">
                  Attention: Cette categorie contient des sous-categories.
                </span>
              )}
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="ghost" onClick={() => setIsDeleteModalOpen(false)}>
                Annuler
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                isLoading={deleteMutation.isPending}
              >
                Archiver
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
